// Frontend client for the WxCC AI Ops backend Lambdas.
//
// Currently wired:
//   POST /ccutils/wxcc-ai-session-summary — Bedrock Sonnet 4.6 summary of
//     a single AI Agent session, driven by the Operation MCP + audit + timeline.
//
// Planned (not yet built):
//   POST /ccutils/wxcc-mcp-proxy — general MCP call proxy so the browser
//     can enumerate agents / sessions without ever holding the WxCC token
//     server-side. Once shipped, this file grows a `mcpCall()` helper.
//
// Auth to the API Gateway: shared MSAL Bearer flow used by every other
// CC Utils tool. The WxCC OAuth token and Operation MCP URL live on the
// Lambda side (env vars for MVP, Secrets Manager for prod) — the browser
// never holds them.

const API_BASE = process.env.REACT_APP_CCUTILS_API_BASE_URL || '';

export function isWxccAiOpsBackendConfigured() {
  return !!API_BASE;
}

/**
 * Generate an AI summary of a single WxCC AI Agent session.
 *
 * @param {object} args
 * @param {string} args.agentId — from wxcc-operations-list-ai-agents.
 * @param {string} args.sessionId — from wxcc-operations-list-ai-agent-sessions.
 * @param {Function} args.getApiToken — useAuth().getApiToken
 *
 * @returns {Promise<{ summary: string, modelId: string, durationMs: number, sourcesUsed: object }>}
 */
export async function summarizeAiAgentSession({ agentId, sessionId }, getApiToken) {
  if (!API_BASE) {
    throw new Error('CC Utils API URL is not configured. Set REACT_APP_CCUTILS_API_BASE_URL.');
  }
  if (!agentId || !sessionId) throw new Error('agentId and sessionId are required.');

  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = await getApiToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to acquire MSAL token:', e?.message || e);
  }

  const res = await fetch(`${API_BASE}/ccutils/wxcc-ai-session-summary`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ agentId, sessionId }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Summary Lambda ${res.status}: ${text.slice(0, 400)}`);
  }
  return res.json();
}

/**
 * Generate a unified AI summary for a whole call — all linked AI Agent
 * sessions + CC-side task/leg context + the Operation MCP audit for each
 * session. The Lambda handles the fan-out server-side (half the browser's
 * rate-limit burden) and feeds everything into Bedrock Sonnet 4.6.
 *
 * @param {object} args
 * @param {string} args.interactionId
 * @param {number} args.from — epoch ms (same window the search used)
 * @param {number} args.to — epoch ms
 * @param {Function} getApiToken — useAuth().getApiToken
 */
export async function summarizeCall({ interactionId, from, to }, getApiToken) {
  return callSummaryLambda({ interactionId, from, to }, getApiToken);
}

/**
 * Diagnose mode — same input shape as summarizeCall, but asks the Lambda
 * to run the pattern-detection + root-cause rubric instead of the plain
 * narrative. Returns the same envelope plus `patterns: string[]` for
 * UI badges.
 */
export async function diagnoseCall({ interactionId, from, to }, getApiToken) {
  return callSummaryLambda({ interactionId, from, to, mode: 'diagnose' }, getApiToken);
}

/**
 * Report mode — server-side fan-out that the browser can't practically do
 * itself (rate-limited audit-ai-agent per session). Returns a report-
 * specific JSON shape; see the Lambda's botToolAuditReport for details.
 *
 * Supported reports:
 *   - 'bot-tool-audit' — aggregated per-bot tool invocation + latency.
 */
export async function fetchReport({ report, from, to, sampleSize }, getApiToken) {
  if (!report) throw new Error('report is required.');
  return callSummaryLambda({ report, from, to, sampleSize }, getApiToken);
}

/**
 * Agent tuning report — analyses one bot's config + sampled problematic
 * sessions and emits evidence-grounded tuning suggestions. The pasted
 * instructions are optional but strongly recommended: Cisco gates the
 * agent's `instruction` field at NOT_RETURNED, so without the paste the
 * suggestions are limited to action-level + prompt-level changes.
 */
export async function fetchAgentTuning({ agentId, from, to, pastedInstructions, sampleSize }, getApiToken) {
  if (!agentId) throw new Error('agentId is required.');
  return callSummaryLambda(
    { report: 'agent-tuning', agentId, from, to, pastedInstructions, sampleSize },
    getApiToken,
  );
}

async function callSummaryLambda(payload, getApiToken) {
  if (!API_BASE) {
    throw new Error('CC Utils API URL is not configured. Set REACT_APP_CCUTILS_API_BASE_URL.');
  }
  const isReportMode = !!payload.report;
  if (!isReportMode && !payload.interactionId) throw new Error('interactionId is required.');
  if (payload.from == null || payload.to == null) throw new Error('from/to (epoch ms) are required.');

  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = await getApiToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to acquire MSAL token:', e?.message || e);
  }

  const res = await fetch(`${API_BASE}/ccutils/wxcc-ai-session-summary`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Summary Lambda ${res.status}: ${text.slice(0, 400)}`);
  }
  return res.json();
}
